import { assetUrl } from './asset-url.js';

const SUPPORTED_ELEMENTS = new Set([
  'heading',
  'paragraph',
  'quote',
  'image',
  'caption',
  'link',
  'hotspot'
]);

function createTextElement(tagName, className, content) {
  const element = document.createElement(tagName);
  element.className = className;
  element.textContent = content ?? '';
  return element;
}

export function safeExternalHref(rawHref) {
  if (!rawHref) return null;

  try {
    const url = new URL(rawHref, window.location.origin);
    if (!['http:', 'https:', 'mailto:'].includes(url.protocol)) return null;
    return url.href;
  } catch {
    return null;
  }
}

function renderElement(item) {
  if (!item || !SUPPORTED_ELEMENTS.has(item.type)) return null;

  switch (item.type) {
    case 'heading': {
      const level = Math.min(6, Math.max(2, Number(item.level) || 2));
      return createTextElement(`h${level}`, 'book-copy book-heading', item.content);
    }
    case 'paragraph':
      return createTextElement('p', 'book-copy book-paragraph', item.content);
    case 'quote': {
      const quote = createTextElement('blockquote', 'book-copy book-quote', item.content);
      if (item.attribution) {
        quote.append(createTextElement('cite', 'book-quote__cite', item.attribution));
      }
      return quote;
    }
    case 'caption':
      return createTextElement('p', 'book-copy book-caption', item.content);
    case 'image': {
      const figure = document.createElement('figure');
      figure.className = 'book-figure';

      const image = document.createElement('img');
      image.src = assetUrl(item.src);
      image.alt = item.alt ?? '';
      image.loading = item.loading === 'eager' ? 'eager' : 'lazy';
      image.decoding = 'async';
      figure.append(image);

      if (item.caption) {
        figure.append(createTextElement('figcaption', 'book-caption', item.caption));
      }
      return figure;
    }
    case 'link':
    // In the linear reader a hotspot is just its link: same destination,
    // no dependence on the raster page or on hover effects.
    case 'hotspot': {
      const href = safeExternalHref(item.href);
      if (!href) return null;
      const link = createTextElement('a', 'book-link', item.label || item.href);
      link.href = href;
      if (new URL(href).origin !== window.location.origin) {
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
      }
      return link;
    }
    default:
      return null;
  }
}

/**
 * An interactive area pinned over a region of the page scan (a printed QR
 * code). Rendered as a real link; the image inside is the QR's dark modules
 * with transparency, which "lift off" the paper on hover/focus while the
 * ::before paper patch keeps the spot underneath clean.
 */
function renderHotspot(item) {
  const href = safeExternalHref(item.href);
  const rect = item.rect;
  if (!href || !rect) return null;

  const link = document.createElement('a');
  link.className = 'book-page__hotspot';
  // A one-line text credit is only a few px tall on screen; the full QR-sized
  // lift would slam it into the neighbouring caption line. Small hotspots get
  // a gentler, scale-based lift instead (see style.css).
  if (rect.h < 6) link.classList.add('book-page__hotspot--small');
  link.href = href;
  link.target = '_blank';
  link.rel = 'noopener noreferrer';
  link.setAttribute('aria-label', item.label || href);

  link.style.left = `${rect.x}%`;
  link.style.top = `${rect.y}%`;
  link.style.width = `${rect.w}%`;
  link.style.height = `${rect.h}%`;
  // The paper patch shows exactly the texture region this box covers, so the
  // page keeps its paper when the modules float up (see .book-page__hotspot
  // in style.css for the background-size/position mapping).
  if (typeof item.patch === 'string') {
    // A dedicated patch image (e.g. inpainted photo behind white ink) simply
    // fills the box.
    link.style.setProperty('--patch-image', `url("${assetUrl(item.patch)}")`);
    link.style.setProperty('--patch-size', '100% 100%');
    link.style.setProperty('--patch-pos', '0% 0%');
  } else {
    link.style.setProperty('--patch-size', `${(100 / rect.w) * 100}% ${(100 / rect.h) * 100}%`);
    link.style.setProperty(
      '--patch-pos',
      `${rect.w >= 100 ? 0 : (rect.x / (100 - rect.w)) * 100}% ${rect.h >= 100 ? 0 : (rect.y / (100 - rect.h)) * 100}%`
    );
  }

  const image = document.createElement('img');
  image.className = 'book-page__hotspot-img';
  image.src = assetUrl(item.image);
  image.alt = '';
  image.loading = 'lazy';
  image.decoding = 'async';
  image.draggable = false;
  link.append(image);

  return link;
}

function createPageArticle(page, index, reader = false) {
  const article = document.createElement('article');
  article.className = reader ? 'reader-page' : 'book-page';
  article.dataset.pageId = page.id;
  article.dataset.pageIndex = String(index);

  if (page.type === 'cover') {
    article.dataset.density = 'hard';
    article.classList.add('book-page--cover');
  }

  if (page.layout) article.dataset.layout = page.layout;

  // Optional page artwork. It sits behind the copy rather than replacing it —
  // pages must stay real DOM text, so a supplied spread image is a background,
  // not a substitute for the content.
  if (!reader && typeof page.background?.src === 'string') {
    const background = document.createElement('img');
    background.className = 'book-page__background';
    // A spread scan is one wide image; `side` picks which half this page
    // shows. The artwork is multiplied over the paper texture in CSS, so a
    // flat print file picks up stock and lighting without manual compositing.
    const side = page.background.side;
    if (side === 'left' || side === 'right') {
      article.classList.add('book-page--photo', `book-page--photo-${side}`);
      background.classList.add(`book-page__background--${side}`);
    }
    background.src = assetUrl(page.background.src);
    background.alt = page.background.alt ?? '';
    background.loading = index === 0 ? 'eager' : 'lazy';
    background.decoding = 'async';
    background.draggable = false;
    article.append(background);
  }

  const inner = document.createElement('div');
  inner.className = reader ? 'reader-page__inner' : 'book-page__inner';

  for (const item of page.elements ?? []) {
    // In book mode a hotspot is pinned over the artwork, not placed in the
    // (visually hidden on photo pages) text flow.
    if (!reader && item?.type === 'hotspot') {
      const hotspot = renderHotspot(item);
      if (hotspot) article.append(hotspot);
      continue;
    }
    // A caption belongs to a photograph, and reader mode shows no
    // photographs: on its own it reads as a stray line dropped between two
    // paragraphs, which is exactly how it looked.
    if (reader && item?.type === 'caption') continue;
    const rendered = renderElement(item);
    if (rendered) inner.append(rendered);
  }

  if (!inner.childElementCount && page.title) {
    inner.append(createTextElement('h2', 'book-heading', page.title));
  }

  article.append(inner);
  return article;
}

export function renderBookPages(container, data) {
  container.replaceChildren();
  const fragment = document.createDocumentFragment();

  data.pages.forEach((page, index) => {
    fragment.append(createPageArticle(page, index, false));
  });

  container.append(fragment);
}

/**
 * Reader mode is the book as one long text, so only pages that actually carry
 * text take part. Every `.reader-page` draws a rule beneath itself; rendering
 * the photo pages too meant a run of rules with nothing between them.
 *
 * A page qualifies if it has body copy, or if it is a block of links — page 82
 * carries the three QR codes (the film, the opening video, the studio site),
 * which are real content in a text version. A lone link is a photo credit and
 * is left out with the captions.
 */
function readerPageHasContent(page) {
  const elements = page.elements ?? [];
  const body = elements.filter((e) => ['heading', 'paragraph', 'quote'].includes(e?.type));
  if (body.length) return true;
  return elements.filter((e) => e?.type === 'hotspot').length > 1;
}

export function renderReader(container, data) {
  container.replaceChildren();
  const fragment = document.createDocumentFragment();

  data.pages.forEach((page, index) => {
    if (!readerPageHasContent(page)) return;
    fragment.append(createPageArticle(page, index, true));
  });

  container.append(fragment);
}
